import { push } from '@lagunovsky/redux-react-router'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import HomeAction from 'home/actions/HomeAction'
import { omit, orderBy } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { H_MAT_SUBSCRIPTION } from '../../../../account/constants/AccessRulesConstants'
import UpdatePanel from '../../../../components/updatePanel/UpdatePanel'
import { componentHasHabilitations } from '../../../../utils/HabilitationUtil'
import { getDate } from '../../../../utils/DateUtil'
import { updateMaterialize } from '../../../../utils/MaterializeUtils'
import { getUser } from '../../../../utils/SettingUtils'
import { isSaveBlocked } from '../../../utils/MaterielUtils'
import { SUBSCRIPTION_ID } from 'materiel/constants/MaterielConstants'
import AdministratorPanel from '../../administrator/AdministratorPanel'
import AssigmentChartPanel from '../../chart/AssigmentChartPanel'
import LastSituationMarkerPanel from '../../situation/LastSituationMarkerPanel'
import SituationPanel from '../../situation/SituationPanel'
import SimAction from '../../sim/actions/SimAction'
import SubscriptionAction from '../actions/SubscriptionAction'
import SubscriptionDto from '../dto/SubscriptionDto'
import SimSubscriptionTable from './SimSubscriptionTable'
import SubscriptionPanel from './SubscriptionPanel'

const SubscriptionApp = () => {
    const [newSubscription, setNewSubscription] = useState({})
    const [isEditMode, setIsEditMode] = useState(false)

    const {
        subscription,
        subscriptionTypes,
        subscriptions,
        subscriptionSituations,
        subscriptionsLastSituations,
        simsLink,
        sims,
        simTypes,
        materielSettingRules,
    } = useSelector(store => ({
        subscription: store.SubscriptionReducer.subscription,
        subscriptionTypes: store.SubscriptionReducer.subscriptionTypes,
        subscriptions: store.SubscriptionReducer.subscriptions,
        subscriptionSituations: store.SubscriptionReducer.subscriptionSituations,
        subscriptionsLastSituations: store.SubscriptionReducer.subscriptionsLastSituations,
        simsLink: store.SubscriptionReducer.simsLink,
        sims: store.SimReducer.sims,
        simTypes: store.SimReducer.simTypes,
        materielSettingRules: store.MaterielReducer.materielSettingRules,
    }))

    const dispatch = useDispatch()
    const params = useParams()

    const fetchSubscription = id => {
        dispatch(SubscriptionAction.fetchSubscription(id)).then(json => {
            if (json.idSubscription) {
                dispatch(SubscriptionAction.fetchSubscriptionSituations(json.idSubscription))
                dispatch(SubscriptionAction.fetchSimsLink(json.idSubscription))
                setNewSubscription(new SubscriptionDto(json))
            }
        })
    }

    const changeAdministrator = managerCode => setNewSubscription(prev => ({ ...prev, managerCode }))

    const changeSubscriptionValue = changes => setNewSubscription(prev => ({ ...prev, ...changes }))

    const deleteSituation = id => dispatch(SubscriptionAction.deleteSubscriptionSituation(id, params.id))

    const filterConsultant = actions => getUser().consultant === '1'
        ? omit(actions, ['save', 'edit', 'delete', 'replace'])
        : actions

    useActions(() => {
        updateMaterialize()
        if (params.id === 'new' && !subscription?.id) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === SUBSCRIPTION_ID)
                    if (isSaveBlocked(subscriptions, newSubscription, rules, 'subscriptionType')) return

                    dispatch(SubscriptionAction.createSubscription(newSubscription)).then(id => {
                        if (id) {
                            const situation = {
                                idSubscription: id,
                                situationDate: newSubscription.purchaseDate,
                                statusCode: 2,
                            }
                            dispatch(SubscriptionAction.createSubscriptionSituation(situation))
                        }
                    })
                },
                cancel: () => {
                    dispatch(push('/materiel/subscription'))
                },
            })
        }
        if (isEditMode) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === SUBSCRIPTION_ID)
                    if (isSaveBlocked(subscriptions, newSubscription, rules, 'subscriptionType')) return

                    const type = subscriptionTypes.find(({ id }) => id === newSubscription.subscriptionType)
                    const currentLink = simsLink.find(({ endDate }) => !endDate)
                    if (type?.label === 'RTC' && type?.label !== 'Ligne Numérique/Box' && currentLink) {
                        dispatch(SimAction.closeLastSimSubscription(currentLink.idSim)).then(() => {
                            dispatch(SubscriptionAction.fetchSimsLink(newSubscription.id))
                        })
                    }
                    dispatch(SubscriptionAction.updateSubscription(newSubscription)).then(success => success && setIsEditMode(false))
                },
                cancel: () => {
                    setNewSubscription(subscription)
                    setIsEditMode(false)
                },
            })
        }
        return filterConsultant({
            edit: () => {
                setIsEditMode(true)
            },
            delete: () => {
                dispatch(SubscriptionAction.deleteSubscription(params.id))
            },
            replace: () => {
                dispatch(ToastrAction.info(i18n.inDeveloppmentFunctionnality))
            },
        })
    }, [isEditMode, newSubscription, subscription, subscriptions, subscriptionTypes, simsLink, materielSettingRules])

    useTitle(() => {
        const { subscriptionType, numLine, id } = subscription
        const title = numLine || id
        const type = (subscriptionTypes.find(t => t.code === parseInt(subscriptionType)) || {}).label || i18n.notDefined
        return ([
            { title: i18n.materiels, href: 'materiel' },
            { title: i18n.subscriptions, href: 'materiel/subscription' },
            { title: type, href: `materiel/subscription/${params.id}` },
            { title, href: `materiel/subscription/${params.id}` },
        ])
    }, [subscription, subscriptionTypes])

    useEffect(() => {
        if (!componentHasHabilitations(H_MAT_SUBSCRIPTION)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return undefined
        }
        if (params.id === 'new') {
            setIsEditMode(true)
        }
        if (!subscriptions.length) {
            dispatch(SubscriptionAction.fetchSubscriptions())
        }
        if (!subscriptionTypes.length) {
            dispatch(SubscriptionAction.fetchSubscriptionTypes())
        }
        if (!sims.length) {
            dispatch(SimAction.fetchSims(true))
        }
        if (!simTypes.length) {
            dispatch(SimAction.fetchSimTypes())
        }
        dispatch(HomeAction.setHelpLink('materiel', ''))
        return () => dispatch(SubscriptionAction.resetSubscription())
    }, [])

    useEffect(() => {
        if (params.id !== 'new') {
            fetchSubscription(params.id)
        }
    }, [params.id])

    const getMaterielNumber = ({ reference }) => reference || ''

    const getSubscriptionLabel = sub => {
        const number = getMaterielNumber(sub)
        if (sub.subscriptionType) {
            const type = subscriptionTypes.find(t => t.id === sub.subscriptionType)
            if (type) {
                return `${type.label} - ${number}`
            }
        }
        return number
    }

    const getSubscriptionsFormat = () => subscriptions.map(sub => ({
        id: sub.id,
        label: getSubscriptionLabel(sub),
    }))

    const onDeleteSimSubsciption = ({ idSim, idSubscription }) => {
        dispatch(SubscriptionAction.deleteSimSubscriptionLink(idSim, idSubscription)).then(() => dispatch(SubscriptionAction.fetchSimsLink(params.id)))
    }

    const formatedLinks = orderBy(simsLink, [({ startDate }) => startDate ? startDate : 0, ({ endDate }) => endDate ? endDate : 0], ['desc', 'desc']).map(({ idSim, idSubscription, startDate, endDate, comment }) => {
        const sim = sims.find(({ id }) => idSim === id)
        if (sim) {
            const type = simTypes.find(({ id }) => id === sim.simType)
            return {
                sim: `${type ? `${type.name}:` : ''} ${sim.serialNumber || ''}`,
                startDate: getDate(startDate),
                endDate: getDate(endDate),
                comment,
                idSim,
                idSubscription,
            }
        }
        return {
            startDate: getDate(startDate),
            endDate: getDate(endDate),
            comment,
            idSim,
            idSubscription,
        }
    })

    const formatedSims = sims.filter(({ isEquipment }) => !isEquipment).map(({ serialNumber = '', id, simType }) => {
        const type = simTypes.find(({ code }) => code === simType)
        return {
            code: id,
            name: `${type ? `${type.name}:` : ''} ${serialNumber}`,
        }
    })

    const type = subscriptionTypes.find(({ id }) => id === newSubscription.subscriptionType)
    const disabled = { active: isEditMode, disabled: !isEditMode }

    return (
        <Card>
            <CardContent>
                <Grid container size={12} spacing={2}>
                    <Grid container size={9}>
                        <LastSituationMarkerPanel materielSituation={subscriptionSituations} />
                        <Grid container size={'grow'}>
                            <Grid size={12}>
                                <SubscriptionPanel
                                    id={params.id}
                                    subscription={newSubscription}
                                    onChange={changeSubscriptionValue}
                                    {...disabled}
                                />
                            </Grid>
                            {
                                !!subscription.id && type?.label !== 'RTC' && type?.label !== 'Ligne Numérique/Box' && (
                                    <Grid size={12}>
                                        <SimSubscriptionTable
                                            links={formatedLinks}
                                            editMode={isEditMode}
                                            headers={['sim', 'startDate', 'endDate', 'comment']}
                                            title={i18n.sims}
                                            idKeyToRedirect={'idSim'}
                                            redirectTo={'sim'}
                                            associatedMateriels={formatedSims}
                                            onDelete={onDeleteSimSubsciption}
                                            onAdd={({ materielLink, startDate, endDate, comment }) => {
                                                const simSubscription = {
                                                    idSubscription: subscription.id,
                                                    idSim: materielLink,
                                                    startDate,
                                                    endDate,
                                                    comment,
                                                }
                                                dispatch(SimAction.createSimSubscription(simSubscription)).then(() =>
                                                    dispatch(SubscriptionAction.fetchSimsLink(params.id)))
                                            }}
                                        />
                                    </Grid>
                                )
                            }

                        </Grid>
                    </Grid>
                    <Grid size={3}>
                        <Stack spacing={1}>
                            <UpdatePanel
                                updateDate={subscription.updateDate}
                                updateLogin={subscription.updateLogin}
                                jobExecId={subscription.jobExecutionId}
                            />
                            <Card>
                                <CardContent>
                                    <AdministratorPanel
                                        onChange={changeAdministrator}
                                        selectedValue={newSubscription.managerCode}
                                        isActive={isEditMode}
                                    />
                                </CardContent>
                            </Card>
                            <AssigmentChartPanel materielSituation={subscriptionSituations} />
                        </Stack>
                    </Grid>
                    <Grid size={12}>
                        <SituationPanel
                            materielSituation={subscriptionSituations}
                            active={isEditMode}
                            idMateriel={subscription.id}
                            purchaseDate={subscription.purchaseDate}
                            idKey={'idSubscription'}
                            lastSituations={subscriptionsLastSituations}
                            materials={getSubscriptionsFormat()}
                            saveFunction={situation => dispatch(SubscriptionAction.createSubscriptionSituation(situation))}
                            deleteFunction={deleteSituation}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

export default SubscriptionApp
